import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { Plus, Download, Trash2, Loader2, Image, Calendar, MessageCircle, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssetLibrary = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [messageStats, setMessageStats] = useState(null);
  const [showMessageBanner, setShowMessageBanner] = useState(true);

  useEffect(() => {
    fetchAssets();
    fetchMessageStats();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessageStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessageStats(response.data);
    } catch (error) {
      // Silently fail - not critical
      console.log('Could not fetch message stats');
    }
  };

  const dismissMessageBanner = async () => {
    setShowMessageBanner(false);
    try {
      await axios.post(`${API_URL}/api/messages/mark-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silently fail
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/assets/${deleteId}`);
      setAssets(prev => prev.filter(a => a.id !== deleteId));
      toast.success('Asset deleted');
    } catch (error) {
      toast.error('Failed to delete asset');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const canCreateMore = user?.is_subscribed || !user?.free_asset_used;
  const hasUnseenMessages = messageStats?.unseen_message_count > 0;

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <Navbar />
      
      <div className="flex-1 pt-20 px-4 pb-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Message Notification Banner */}
          {hasUnseenMessages && showMessageBanner && (
            <div className="bg-[#0a2540] text-white rounded-xl p-4 mb-6 flex items-center justify-between" data-testid="message-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#d4af37] rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-[#0a2540]" />
                </div>
                <div>
                  <p className="font-semibold">
                    You've had {messageStats.unseen_message_count} new message{messageStats.unseen_message_count !== 1 ? 's' : ''} since your last visit
                  </p>
                  <p className="text-sm text-slate-300">
                    Your posts are working — check your Facebook messages
                  </p>
                </div>
              </div>
              <button 
                onClick={dismissMessageBanner}
                className="text-slate-400 hover:text-white p-1"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#0a2540]">
                My Sales Assets
              </h1>
              <p className="text-slate-600 mt-1">
                {assets.length} asset{assets.length !== 1 ? 's' : ''} saved
              </p>
            </div>
            
            {canCreateMore ? (
              <Button
                onClick={() => navigate('/create')}
                className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-12 px-6"
                data-testid="create-new-asset-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create New Asset
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/subscribe')}
                className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-12 px-6"
                data-testid="subscribe-btn"
              >
                Subscribe for More
              </Button>
            )}
          </div>

          {/* Subscription Nudge */}
          {!user?.is_subscribed && user?.free_asset_used && assets.length > 0 && (
            <div className="bg-[#0a2540] text-white rounded-2xl p-6 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="font-semibold">Most businesses lose enquiries after this stage.</p>
                <p className="text-slate-300 text-sm mt-1">
                  Subscribe to create unlimited assets and keep the momentum going.
                </p>
              </div>
              <Button
                onClick={() => navigate('/subscribe')}
                className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] whitespace-nowrap"
                data-testid="nudge-subscribe-btn"
              >
                £9/month
              </Button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#0a2540]" />
            </div>
          )}

          {/* Empty State */}
          {!loading && assets.length === 0 && (
            <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Image className="w-8 h-8 text-slate-400" />
              </div>
              <h2 className="text-xl font-bold text-[#0a2540] mb-2">
                No assets yet
              </h2>
              <p className="text-slate-600 mb-6 max-w-sm mx-auto">
                Create your first sales asset from your completed job photos.
              </p>
              <Button
                onClick={() => navigate('/create')}
                className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-12 px-6"
                data-testid="empty-create-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Asset
              </Button>
            </div>
          )}

          {/* Assets Grid */}
          {!loading && assets.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {assets.map((asset) => {
                const imageUrl = asset.images?.[0]?.startsWith('http')
                  ? asset.images[0]
                  : `${API_URL}${asset.images?.[0] || ''}`;
                
                return (
                  <div
                    key={asset.id}
                    className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300"
                    data-testid={`asset-card-${asset.id}`}
                  >
                    {/* Image */}
                    <div 
                      className="relative aspect-square cursor-pointer overflow-hidden"
                      onClick={() => navigate(`/editor/${asset.id}`)}
                    >
                      {asset.images?.[0] ? (
                        <>
                          <img
                            src={imageUrl}
                            alt={asset.job_description}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {/* Overlay on hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-sm font-medium line-clamp-2">
                              {asset.generated_copy?.overlay_texts?.[0]}
                            </p>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                          <Image className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <p className="text-sm text-slate-600 line-clamp-1 mb-2">
                        {asset.job_description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Calendar className="w-3 h-3" />
                          {formatDate(asset.created_at)}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => navigate(`/editor/${asset.id}`)}
                            data-testid={`edit-asset-${asset.id}`}
                          >
                            <Download className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-red-50"
                            onClick={() => setDeleteId(asset.id)}
                            data-testid={`delete-asset-${asset.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-slate-500 hover:text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The asset will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};

export default AssetLibrary;
